import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { __ } from "services/Translator";
import { discardOutbox, retryOutbox } from "stores/support/supportSlice";
import { selectSupportOutbox } from "stores/support/supportSelectors";
import useConversation from "support/hooks/useConversation";
import useOutboxPump from "support/hooks/useOutboxPump";
import useTransport from "support/hooks/useTransport";
import MessageList from "support/components/MessageList";
import RgHandoffView from "support/views/RgHandoffView";
import TransportPill from "support/components/TransportPill";
import Composer from "support/components/Composer";
import CsatForm from "support/components/CsatForm";

export default function ConversationView({ reference, active, config }) {
  const dispatch = useDispatch();
  const { conversation, messages, csat, loading, error, reload } = useConversation(reference);
  const outbox = useSelector(selectSupportOutbox);
  useOutboxPump(!!active);

  // The transport is keyed by the internal uuid, which only the show payload
  // carries; until it arrives there is nothing to subscribe to.
  const highestServerId = messages.length ? messages[messages.length - 1].id : 0;
  const { liveMessages, transportState, nudge } = useTransport({
    uuid: conversation ? conversation.uuid : null,
    config,
    sinceId: highestServerId,
    active: !!active && !!conversation,
  });

  const pending = useMemo(
    () => outbox.filter((item) => item.ref === reference),
    [outbox, reference]
  );

  // Optimistic rows carry the same shape the server would return, so
  // MessageBubble needs no branch for them. delivery_status is what tells the
  // bubble a send is still in flight or has given up.
  const combined = useMemo(
    () =>
      messages
        .concat(liveMessages.filter((live) => !messages.some((m) => m.id === live.id)))
        .concat(
        pending.map((item) => ({
          tempId: item.tempId,
          id: null,
          body: item.body,
          author_type: "player",
          author_name: null,
          visibility: "public",
          created_at: item.at,
          delivery_status: item.status === "failed" ? "failed" : "sending",
        }))
      ),
    [messages, liveMessages, pending]
  );

  // A queued message leaves the outbox the moment the server acks it, so pull
  // the durable row in straight away. Without this the bubble would blink out
  // and only reappear on the next poll.
  const pendingCount = pending.length;
  const previousPending = useRef(pendingCount);
  useEffect(() => {
    if (pendingCount < previousPending.current) {
      reload();
      nudge();
    }
    previousPending.current = pendingCount;
  }, [pendingCount, reload, nudge]);

  const failed = pending.filter((item) => item.status === "failed");
  const features = (config && config.features) || {};
  const csatOffered = !!(features.csat && csat && csat.eligible);
  const attachmentsEnabled = !!features.attachments;
  const closed = conversation && ["closed", "solved"].indexOf(conversation.status) !== -1;

  if (loading && !conversation) {
    return (
      <div className="supState" role="status">
        {__("Loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="supState supStateError" role="status">
        <p>
          {error === 404
            ? __("This conversation is no longer available.")
            : __("We could not load this conversation.")}
        </p>
        {error !== 404 && (
          <button type="button" className="supBtn supBtnGhost" onClick={reload}>
            {__("Try again")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="supThread">
      {conversation && (
        <div className="supThreadHead">
          <p className="supThreadSubject">{conversation.subject || __("Conversation")}</p>
          <p className="supThreadRef">
            {conversation.reference}
            {conversation.status ? " · " + conversation.status : ""}
          </p>
          <TransportPill state={transportState} />
        </div>
      )}

      {conversation && conversation.rg_handoff && (
        <RgHandoffView config={config} />
      )}

      <div className="supThreadBody">
        <MessageList messages={combined} />
      </div>

      {failed.map((item) => (
        <div key={item.tempId} className="supRetryBar" role="status">
          <span>{__("Your message was not sent.")}</span>
          <button
            type="button"
            className="supLinkBtn"
            onClick={() => dispatch(retryOutbox(item.tempId))}
          >
            {__("Retry")}
          </button>
          <button
            type="button"
            className="supLinkBtn"
            onClick={() => dispatch(discardOutbox(item.tempId))}
          >
            {__("Discard")}
          </button>
        </div>
      ))}

      {csatOffered && <CsatForm reference={reference} onSubmitted={reload} />}

      {closed ? (
        <p className="supClosedNote">{__("This conversation is closed. Start a new one to reply.")}</p>
      ) : (
        <Composer reference={reference} attachmentsEnabled={attachmentsEnabled} />
      )}
    </div>
  );
}
